import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Lock, NotebookPen, Plus } from 'lucide-react';
import { staffManagerApiService } from '../../../services/staffManagerApi';
import type { StaffNote, StaffNoteType } from '../../../types/staffManager';
import { SkeletonLoader } from '../../SkeletonLoader';
import { ConfirmationModal } from '../../ConfirmationModal';
import { StaffNoteCard, NOTE_TYPE_META } from './StaffNoteCard';
import { StaffNoteModal, type StaffNoteSubjectRef } from './StaffNoteModal';
import { cn } from '../../../utils/cn';

interface StaffNotesPanelProps {
  subject: StaffNoteSubjectRef;
  canWrite: boolean;
  /** Bump to refetch (e.g. after the parent creates a note elsewhere). */
  refreshNonce?: number;
  /** Called with the loaded notes so parents can show counts. */
  onLoaded?: (notes: StaffNote[]) => void;
  /** Wrap in the amber locked frame with a heading (player page). Off inside the Staff Manager, which has its own sections. */
  framed?: boolean;
  /** Hide the whole panel while empty (player page for non-staff players). */
  hideWhenEmpty?: boolean;
  className?: string;
}

type TypeFilter = 'all' | StaffNoteType;

/**
 * Staff Manager Notes for one subject: list, type filter, create / edit / delete for writers.
 * Requires the viewer to hold argus.staff.notes.read — the backend refuses otherwise.
 */
export const StaffNotesPanel = ({ subject, canWrite, refreshNonce = 0, onLoaded, framed = false, hideWhenEmpty = false, className }: StaffNotesPanelProps) => {
  const [notes, setNotes] = useState<StaffNote[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TypeFilter>('all');
  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; note: StaffNote } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<StaffNote | null>(null);
  const [deleting, setDeleting] = useState(false);

  const subjectKey = `${subject.discord_id ?? ''}|${subject.uuid ?? ''}|${subject.user_id ?? ''}`;
  const hasSubject = !!(subject.discord_id || subject.uuid || subject.user_id);

  const load = useCallback(async () => {
    if (!hasSubject) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setError(null);
    try {
      const data = await staffManagerApiService.getNotesForSubject({ discord_id: subject.discord_id, uuid: subject.uuid, user_id: subject.user_id });
      setNotes(data);
      onLoaded?.(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Staff Manager Notes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectKey, hasSubject, refreshNonce]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const visible = useMemo(() => (notes ?? []).filter((n) => filter === 'all' || n.type === filter), [notes, filter]);
  const counts = useMemo(() => {
    const c: Record<TypeFilter, number> = { all: notes?.length ?? 0, note: 0, warning: 0, commendation: 0 };
    for (const n of notes ?? []) c[n.type] += 1;
    return c;
  }, [notes]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await staffManagerApiService.deleteNote(pendingDelete.id);
      setNotes((prev) => (prev ?? []).filter((n) => n.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete the note');
    } finally {
      setDeleting(false);
    }
  };

  const onSaved = (saved: StaffNote) => {
    setNotes((prev) => {
      const list = prev ?? [];
      const idx = list.findIndex((n) => n.id === saved.id);
      if (idx === -1) return [saved, ...list];
      const next = list.slice();
      next[idx] = saved;
      return next;
    });
  };

  if (hideWhenEmpty && !loading && !error && (notes?.length ?? 0) === 0) return null;

  const body = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-glass-200/20 p-0.5" role="group" aria-label="Filter notes by type">
          {(['all', 'note', 'warning', 'commendation'] as TypeFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={cn(
                'h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors touch-manipulation capitalize',
                filter === f ? 'bg-accent-500/20 text-accent-300' : 'text-glass-400 hover:text-white',
              )}
            >
              {f === 'all' ? 'All' : NOTE_TYPE_META[f].label}
              <span className="ml-1 font-mono text-[10px] opacity-70">{counts[f]}</span>
            </button>
          ))}
        </div>
        {canWrite && hasSubject && (
          <button type="button" onClick={() => setModal({ mode: 'create' })} className="glass-button-sm ml-auto inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add note
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <SkeletonLoader className="h-24 w-full" />
          <SkeletonLoader className="h-24 w-full" />
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-glass-200/20 p-6 text-center text-sm text-glass-500">
          <NotebookPen className="w-6 h-6 mx-auto mb-2 text-glass-500" />
          {counts.all === 0 ? 'No Staff Manager Notes yet.' : 'No notes of this type.'}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((n) => (
            <StaffNoteCard key={n.id} note={n} canWrite={canWrite} onEdit={(note) => setModal({ mode: 'edit', note })} onDelete={setPendingDelete} />
          ))}
        </div>
      )}

      <StaffNoteModal
        isOpen={modal !== null}
        mode={modal?.mode ?? 'create'}
        note={modal?.mode === 'edit' ? modal.note : null}
        subject={subject}
        onClose={() => setModal(null)}
        onSaved={onSaved}
      />
      <ConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Delete this note?"
        message="The note is removed permanently. The deletion itself is audit-logged (without the content)."
        confirmText="Delete"
        isLoading={deleting}
      />
    </>
  );

  if (!framed) return <div className={cn('space-y-3', className)}>{body}</div>;

  return (
    <section className={cn('glass-card relative overflow-hidden border border-amber-500/20', className)}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" aria-hidden="true" />
      <header className="flex flex-wrap items-center gap-3 px-4 pt-4 md:px-5 md:pt-5">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500/15 text-amber-300 flex-shrink-0">
          <Lock className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white tracking-wide">
            Staff Manager Notes
            <span className="ml-2 text-xs font-mono font-normal text-glass-400 tabular-nums">{counts.all}</span>
          </h3>
          <p className="text-[11px] text-glass-500">Confidential — visible only to holders of argus.staff.notes.read.</p>
        </div>
      </header>
      <div className="px-4 pb-4 pt-3 md:px-5 md:pb-5 space-y-3">{body}</div>
    </section>
  );
};
